import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../elements/Button';
import Icon from '../common/Icon';
import Loading from '../common/Loading';
import { apiRequest, apiErrorText } from '../../utils';

/* Send the information pack before the rep reaches the car.

   From the rep's OWN mailbox, so the reply comes back to them, with the general
   address in copy so support can pick the thread up without being forwarded
   anything. That copy is pre-filled from the unit's answering address and stays
   editable — clearing it is a decision the rep is allowed to make.

   The subject is shown because a template's subject is what the recipient sees
   first; the body is not, because it is stored HTML with merge tokens and a
   phone is the wrong place to edit it. Picking the right template is the job. */
const MailSheet = ({ company, contact, buId, onClose, onSent }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [cc, setCc] = useState('');
  const [mailbox, setMailbox] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [box, lib] = await Promise.all([
          apiRequest('GET', `/sales/mailbox?bu_id=${buId}`),
          apiRequest('GET', `/sales/mail/templates?bu_id=${buId}`),
        ]);
        setMailbox(box);
        const list = lib.templates || [];
        setTemplates(list);
        setCc(lib.default_cc || '');
        // One template is not a choice. Most units tag exactly one mail as the
        // visit pack, and asking a rep on a pavement to open a dropdown to
        // confirm the only entry is a tap that decides nothing — so the sheet
        // opens ready to send. Read off `list`, not the state that was just set:
        // it has not landed yet, and pick() would find nothing to take the
        // subject from.
        if (list.length === 1) {
          setTemplateId(list[0].id);
          setSubject(list[0].subject || '');
        }
      } catch (err) {
        setError(apiErrorText(t, err, 'somethingWentWrong'));
      } finally {
        setLoading(false);
      }
    })();
  }, [buId, t]);

  const pick = (id) => {
    setTemplateId(id);
    const tpl = templates.find((x) => x.id === id);
    // Show the template's own subject, editable. Merge tokens are rendered
    // server-side at send time, so {{first_name}} here is expected, not a bug.
    setSubject(tpl?.subject || '');
  };

  const send = async () => {
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await apiRequest('POST', `/sales/company/${company.id}/mail`, {
        bu_id: buId,
        person_id: contact.id,
        template_id: templateId || undefined,
        subject: subject || undefined,
        // Always sent explicitly: the server only falls back to the unit's
        // address when the field is absent, so an empty string here means the
        // rep cleared it on purpose and nobody is copied in.
        cc,
      });
      onSent(res);
    } catch (err) {
      setError(apiErrorText(t, err, 'somethingWentWrong'));
      setSending(false);
    }
  };

  const connected = mailbox?.connected;

  return (
    <div className="sales-sheet" role="dialog" aria-modal="true">
      <div className="sales-sheet__panel">
        <div className="sales-sheet__head">
          <h5>{t('salesSendInfo')}</h5>
          <button type="button" className="sales-sheet__close" onClick={onClose}
                  aria-label={t('close')}>
            <Icon name="cross" />
          </button>
        </div>

        <div className="sales-sheet__body">
          {loading && <Loading />}

          {!loading && !connected && (
            <>
              <p className="sales-hint">{t('salesMailboxNotConnected')}</p>
              <Button mb="0" onClick={() => {
                window.location.href = `/api/sales/mailbox/start?bu_id=${buId}`;
              }}>
                <Icon name="envelope" /> {t('salesConnectMailbox')}
              </Button>
            </>
          )}

          {!loading && connected && (
            <>
              <p className="sales-sheet__company">
                {contact.name} · <span className="sales-result__sub">{contact.email}</span>
              </p>
              <p className="sales-hint">
                {t('salesSendingFrom', { address: mailbox.account_email })}
              </p>

              <label className="sales-label">{t('salesTemplate')}</label>
              <select className="sales-input" value={templateId}
                      onChange={(e) => pick(e.target.value)}>
                {/* No "pick one" line when there is only one — it is already
                    picked, and offering to un-pick it is the tap this change
                    exists to remove. Still shown at 0, where it reads as the
                    empty state. */}
                {templates.length !== 1 && (
                  <option value="">{t('salesPickTemplate')}</option>
                )}
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                ))}
              </select>
              {/* The list is customer-visit templates and nothing else, so an
                  empty one means nobody has tagged a mail yet — not that this
                  unit has no mail at all. Say which, or a rep goes looking in
                  the wrong place. */}
              {!templates.length && (
                <p className="sales-hint">{t('salesNoTemplates')}</p>
              )}

              <label className="sales-label">{t('salesSubject')}</label>
              <input className="sales-input" value={subject}
                     onChange={(e) => setSubject(e.target.value)} />

              <label className="sales-label">{t('salesCopyTo')}</label>
              <input className="sales-input" value={cc} type="email"
                     onChange={(e) => setCc(e.target.value)} />
              <p className="sales-hint">{t('salesCopyToWhy')}</p>

              {error && <p className="sales-error">{error}</p>}
            </>
          )}
        </div>

        {!loading && connected && (
          <div className="sales-sheet__foot">
            <Button theme="secondary" onClick={onClose} mb="0" styles={{ width: '40%' }}>
              {t('cancel')}
            </Button>
            <Button onClick={send} mb="0" styles={{ width: '58%' }}
                    disabled={sending || !templateId || !subject}>
              {sending ? t('salesSending') : t('salesSendNow')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MailSheet;
